import React, { useState, useEffect } from 'react';
import SafeIcon from './common/SafeIcon';
import { FiBook, FiMap, FiBox, FiCrosshair, FiMessageSquare, FiDollarSign, FiHelpCircle, FiPhone, FiSearch } from 'react-icons/fi';
import Overview from './sections/01_Overview';
import PortfolioMap from './sections/02_PortfolioMap';
import DeepDives from './sections/03_DeepDives';
import Playbooks from './sections/04_Playbooks';
import Scripts from './sections/05_Scripts';
import Commercial from './sections/06_Commercial';
import FAQ from './sections/08_FAQ';
import Internal from './sections/07_Internal';
import SearchModal from './components/ui/SearchModal';

export default function Dashboard() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const navItems = [
    { id: 'overview', label: 'Executive Overview', icon: FiBook },
    { id: 'portfolio', label: 'Portfolio Map', icon: FiMap },
    { id: 'deep-dives', label: 'Product Deep Dives', icon: FiBox },
    { id: 'playbooks', label: 'Sales Playbook', icon: FiCrosshair },
    { id: 'scripts', label: 'Scripts & Objections', icon: FiMessageSquare },
    { id: 'commercial', label: 'Commercial & ROI', icon: FiDollarSign },
    { id: 'faq', label: 'Buyer FAQ Bank', icon: FiHelpCircle },
    { id: 'contact', label: 'Contact Founder', icon: FiPhone },
  ];

  const searchItems = [
    { id: 'commercial', category: 'Commercial', title: 'Why no free trial?', content: 'Because we carry significant ongoing costs for technology and content.' },
    { id: 'commercial', category: 'Commercial', title: 'Pricing Structure', content: 'Volume and traffic dependent pricing.' },
    { id: 'faq', category: 'FAQ', title: 'Where do news stories come from?', content: 'External sources + editorial input + original stories.' },
    { id: 'faq', category: 'FAQ', title: 'What is The Pitch Feed?', content: '40+ football news stories per day.' },
    { id: 'faq', category: 'FAQ', title: 'Can we produce original stories?', content: 'Yes, we create fully original football stories.' },
    { id: 'contact', category: 'Support', title: 'Contact Kasper / Founder', content: 'WhatsApp: +4527577601' },
    { id: 'deep-dives', category: 'Product', title: 'ScoreWidget Features', content: 'Live scores, standings, and match data widgets.' },
    { id: 'scripts', category: 'Scripts', title: 'Cold Outreach Template', content: 'Scaling football coverage subject lines.' },
  ];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="flex h-screen bg-slate-950 text-slate-200 overflow-hidden font-sans">
      <aside className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col hidden md:flex h-full print:hidden">
        <div className="p-6 border-b border-slate-800">
          <h1 className="text-xl font-bold text-white tracking-tight">Sales OS</h1>
          <p className="text-xs text-blue-400 font-medium uppercase tracking-wider mt-1">Pitch Feed Ecosystem</p>
        </div>

        <div className="px-4 pt-4">
          <button 
            onClick={() => setIsSearchOpen(true)}
            className="w-full flex items-center justify-between px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-slate-500 hover:text-slate-300 hover:border-slate-700 transition-all group"
          >
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiSearch} size={16} />
              <span className="text-sm">Search...</span>
            </div>
            <kbd className="text-[10px] font-sans bg-slate-800 px-1.5 py-0.5 rounded text-slate-600 group-hover:text-slate-400">⌘K</kbd>
          </button>
        </div>
        
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-3">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => scrollTo(item.id)}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors text-left"
                >
                  <SafeIcon icon={item.icon} className="text-slate-500" />
                  <span>{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button 
            onClick={() => window.print()}
            className="w-full bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium py-2 rounded transition-colors"
          >
            Download Cheat Sheet
          </button>
        </div>
      </aside>

      <main className="flex-1 h-full overflow-y-auto bg-slate-950 relative">
        <header className="sticky top-0 z-10 bg-slate-950/80 backdrop-blur-md border-b border-slate-800 p-4 flex justify-between items-center md:hidden print:hidden">
          <h1 className="font-bold text-white">Sales OS</h1>
          <div className="flex items-center space-x-3">
            <button onClick={() => setIsSearchOpen(true)} className="p-2 bg-slate-800 rounded-lg">
              <SafeIcon icon={FiSearch} size={18} />
            </button>
            <button onClick={() => window.print()} className="text-xs bg-slate-800 px-3 py-1 rounded">Print</button>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-6 md:px-12 pb-24 print:p-0 print:m-0 print:max-w-none">
          <Overview />
          <PortfolioMap />
          <DeepDives />
          <Playbooks />
          <Scripts />
          <Commercial />
          <FAQ />
          <Internal />
        </div>
      </main>

      <SearchModal 
        isOpen={isSearchOpen} 
        onClose={() => setIsSearchOpen(false)} 
        onSelect={scrollTo}
        searchItems={searchItems}
      />
    </div>
  );
}